import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, RefreshCw, Building2, X, Mail, Copy, Check } from 'lucide-react';

const API = '/api';

// Facility Partner leads (hg_partners, Randall 23 Sep 2026) — a working screen, not a CRM.
// List + detail. The status dropdown IS the manual stop path: moving a lead out of
// Not contacted / Email 1–3 sent stops the drip for its address and takes the address out of
// the facility-partners MailerLite group (the server reports whether that removal happened).

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return String(iso).slice(0, 16); }
};

const stepLabel = (n) => (n === null || n === undefined ? '—' : `Email ${n}`);

function DripCell({ drip }) {
  if (!drip) return <span className="text-hg-text-dim">no drip</span>;
  if (drip.status !== 'active') {
    return <span className="text-hg-text-dim" title={drip.stopped_reason || ''}>{drip.status}{drip.last_step != null ? ` after ${stepLabel(drip.last_step)}` : ''}</span>;
  }
  return <span>{stepLabel(drip.next_step)} · {fmt(drip.next_send_at)}</span>;
}

function StartOnboarding({ leadId, onStart }) {
  // Opens the facility onboarding for this lead — a new one, or the one already in progress
  // (the server answers 409 with its id rather than minting a duplicate). Sends nothing.
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const go = async () => {
    setBusy(true); setErr('');
    try {
      const r = await axios.post(`${API}/partners/facilities`, { lead_id: leadId });
      onStart?.(r.data.id);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.facility_id) onStart?.(d.facility_id);
      else setErr((typeof d === 'string' ? d : d?.message) || 'Could not start onboarding.');
    }
    setBusy(false);
  };
  return (
    <div className="flex items-center gap-2">
      <button onClick={go} disabled={busy || !onStart} data-testid="start-onboarding"
        className="inline-flex items-center gap-1 px-3 py-1 text-xs font-sans border border-hg-green text-hg-green rounded-sm disabled:opacity-40">
        {busy ? <Loader2 size={12} className="animate-spin" /> : 'Start onboarding'}
      </button>
      {err && <span className="text-[12px] text-red-400 font-sans">{err}</span>}
    </div>
  );
}

function Detail({ id, statuses, onClose, onChanged, onStartOnboarding }) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setError('');
    try {
      const r = await axios.get(`${API}/partners/leads/${id}`);
      setData(r.data); setStatus(r.data.lead.status || '');
    } catch (e) { setError(e.response?.data?.detail || 'Could not load the lead.'); }
  }, [id]);
  useEffect(() => { load(); setResult(null); setPreview(null); }, [load]);

  const save = async () => {
    setBusy(true); setError(''); setResult(null);
    try {
      const r = await axios.post(`${API}/partners/leads/${id}/status`, { status, note });
      setResult(r.data); setNote(''); await load(); onChanged?.();
    } catch (e) { setError(e.response?.data?.detail || 'Status not changed.'); }
    setBusy(false);
  };

  const showPreview = async (step) => {
    setPreview({ step, html: null, error: '' });
    try {
      const r = await axios.get(`${API}/partners/leads/${id}/preview/${step}`, { responseType: 'text' });
      setPreview({ step, html: r.data, error: '' });
    } catch (e) { setPreview({ step, html: null, error: e.response?.data?.detail || 'Cannot render.' }); }
  };

  if (!data) {
    return <div className="p-4 text-xs text-hg-text-dim font-sans">{error || <Loader2 size={14} className="animate-spin" />}</div>;
  }
  const l = data.lead; const e = data.enrollment;
  const Field = ({ k, v }) => (
    <div className="text-[12px] font-sans"><span className="text-hg-text-dim">{k}: </span><span className="text-hg-text">{v || '—'}</span></div>
  );
  return (
    <div className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-3" data-testid="partner-lead-detail">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-display text-lg text-hg-text tracking-wide">{l.facility}</div>
          <div className="text-[12px] text-hg-text-dim font-sans">{[l.city, l.state].filter(Boolean).join(', ')} · {l.market || l.source}</div>
        </div>
        <button onClick={onClose} className="p-1 text-hg-text-dim hover:text-hg-text"><X size={16} /></button>
      </div>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
        <Field k="Tier" v={l.tier} /><Field k="Owner" v={l.owner} />
        <Field k="Contact" v={[l.first_name, l.last_name].filter(Boolean).join(' ') || l.owner_contact_name} /><Field k="Greeting" v={l.greeting} />
        <Field k="Email" v={l.email} /><Field k="Phone" v={l.phone} />
        <Field k="Bay rate" v={l.bay_rate ? `$${l.bay_rate}` : '$40 (default)'} /><Field k="Time zone" v={l.timezone} />
        <Field k="Next step" v={l.next_step} /><Field k="P.S. from" v={l.hg_contact} />
      </div>
      <StartOnboarding leadId={l.id} onStart={onStartOnboarding} />
      {l.why_it_fits && <div className="text-[12px] font-sans text-hg-text-dim">Why it fits: {l.why_it_fits}</div>}
      {l.notes && <div className="text-[12px] font-sans text-hg-text-dim">Notes: {l.notes}</div>}
      {l.form_notes && (
        <div data-testid="partner-form-notes" className="text-[12px] font-sans">
          <div className="text-[10px] uppercase tracking-wider text-hg-text-dim">Their message (partner form)</div>
          <div className="text-hg-text whitespace-pre-wrap">{l.form_notes}</div>
        </div>
      )}

      <div className="border-t border-hg-border pt-3 space-y-2">
        <div className="text-[10px] uppercase tracking-wider text-hg-text-dim font-sans">Status</div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={status} onChange={(ev) => setStatus(ev.target.value)} data-testid="partner-status-select"
            className="bg-hg-bg border border-hg-border rounded-sm text-[13px] text-hg-text px-2 py-1 font-sans">
            {(statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input value={note} onChange={(ev) => setNote(ev.target.value)} placeholder="note (optional)"
            className="flex-1 min-w-[160px] bg-hg-bg border border-hg-border rounded-sm text-[13px] text-hg-text px-2 py-1 font-sans" />
          <button onClick={save} disabled={busy || status === l.status}
            className="px-3 py-1 text-xs font-sans border border-hg-green text-hg-green rounded-sm disabled:opacity-40">
            {busy ? <Loader2 size={12} className="animate-spin" /> : 'Save status'}
          </button>
        </div>
        {result && (
          <div className="text-[12px] font-sans text-hg-text">
            {result.from} → {result.to}
            {result.enrollment && <> · drip: {result.enrollment}</>}
            {result.mailerlite && <> · MailerLite group: <span className={String(result.mailerlite).startsWith('NOT') ? 'text-red-400' : ''}>{result.mailerlite}</span></>}
          </div>
        )}
        {error && <div className="text-[12px] text-red-400 font-sans">{error}</div>}
        {(l.status_history || []).length > 0 && (
          <div className="text-[11px] font-sans text-hg-text-dim space-y-0.5">
            {[...l.status_history].reverse().slice(0, 6).map((h, i) => (
              <div key={i}>{fmt(h.at)} · {h.status} · {h.by}{h.note ? ` — ${h.note}` : ''}</div>
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-hg-border pt-3 space-y-1">
        <div className="text-[10px] uppercase tracking-wider text-hg-text-dim font-sans">Drip</div>
        {e ? (
          <div className="text-[12px] font-sans text-hg-text space-y-0.5">
            <div>{e.status} · {e.entry_point === 'inbound' ? 'inbound (Email 0, then 3–5)' : 'cold (Emails 1–5)'} · {e.timezone}</div>
            <div>Last: {stepLabel(e.last_step)} {e.last_sent_at ? `· ${fmt(e.last_sent_at)}` : ''} · Next: {e.status === 'active' ? `${stepLabel(e.current_step)} · ${fmt(e.next_send_at)}` : '—'}</div>
            {e.stopped_reason && <div className="text-hg-text-dim">Stopped: {e.stopped_reason}</div>}
          </div>
        ) : <div className="text-[12px] font-sans text-hg-text-dim">Not enrolled{l.email ? '' : ' — no email yet'}.</div>}
        {l.email && (
          <div className="flex items-center gap-1.5 flex-wrap pt-1">
            <span className="text-[11px] text-hg-text-dim font-sans inline-flex items-center gap-1"><Mail size={11} /> Look at:</span>
            {[0, 1, 2, 3, 4, 5].map((n) => (
              <button key={n} onClick={() => showPreview(n)}
                className="px-2 py-0.5 text-[11px] font-sans border border-hg-border rounded-sm text-hg-text-dim hover:border-hg-green hover:text-hg-green">E{n}</button>
            ))}
          </div>
        )}
        {preview && (
          <div className="border border-hg-border rounded-sm bg-white mt-2">
            {preview.error ? <div className="p-3 text-[12px] text-red-600 font-sans">{preview.error}</div>
              : preview.html ? <iframe title={`Email ${preview.step}`} srcDoc={preview.html} className="w-full h-[520px]" sandbox="" />
                : <div className="p-3"><Loader2 size={14} className="animate-spin" /></div>}
          </div>
        )}
      </div>

      {(data.replies || []).length > 0 && (
        <div className="border-t border-hg-border pt-3 space-y-2">
          <div className="text-[10px] uppercase tracking-wider text-hg-text-dim font-sans">Replies</div>
          {data.replies.map((r) => (
            <div key={r.message_id} className="text-[12px] font-sans">
              <div className="text-hg-text-dim">{fmt(r.received_at)} · {r.subject}</div>
              <div className="text-hg-text whitespace-pre-wrap">{(r.body_text || '').slice(0, 1200)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Pieces() {
  const [p, setP] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => { axios.get(`${API}/partners/pieces`).then((r) => setP(r.data)).catch(() => setP(null)); }, []);
  if (!p) return null;
  const copy = async () => {
    try { await navigator.clipboard.writeText(p.t2t_item); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* clipboard blocked */ }
  };
  return (
    <div className="bg-hg-surface border border-hg-border rounded-sm p-3 space-y-2 text-[12px] font-sans">
      <div className="text-[10px] uppercase tracking-wider text-hg-text-dim">Campaign pieces</div>
      <div className="text-hg-text">Teacher-to-Teacher item — draft, you place it:
        <button onClick={copy} className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 border border-hg-border rounded-sm text-hg-text-dim hover:text-hg-green">
          {copied ? <Check size={11} /> : <Copy size={11} />} copy</button>
      </div>
      <div className="text-hg-text-dim italic">{p.t2t_item}</div>
      <div className="text-hg-text">Trade-list email ("{p.trade_subject}"): {p.trade_draft ? `Email Studio draft, ${p.trade_draft.status}` : 'not seeded yet'}</div>
      <div className="text-hg-text">Social drafts in Social Studio: {p.social_drafts.length ? p.social_drafts.join(', ') : 'not seeded yet'}</div>
    </div>
  );
}

export default function PartnerLeads({ onStartOnboarding } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { const r = await axios.get(`${API}/partners/leads`); setData(r.data); }
    catch (e) { setError(e.response?.data?.detail || 'Could not load partner leads.'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = (data?.rows || []).filter((r) => !filter || r.status === filter);
  return (
    <div className="space-y-4" data-testid="partner-leads-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl text-hg-text tracking-wider">FACILITY PARTNERS</h1>
          <p className="text-sm text-hg-text-dim font-sans mt-1">
            Leads for the partner program (hg_partners). Changing a status here stops the drip for that address.
            {data?.drip_gate?.note && <span className="text-amber-300"> {data.drip_gate.note}.</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={filter} onChange={(e) => setFilter(e.target.value)}
            className="bg-hg-bg border border-hg-border rounded-sm text-xs text-hg-text px-2 py-1.5 font-sans">
            <option value="">All statuses</option>
            {(data?.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={load} disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green">
            {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Refresh
          </button>
        </div>
      </div>
      {error && <div className="text-xs text-red-400 font-sans">{error}</div>}
      {open && <Detail id={open} statuses={data?.statuses} onClose={() => setOpen(null)} onChanged={load} onStartOnboarding={onStartOnboarding} />}
      {data && (
        <div className="overflow-x-auto border border-hg-border rounded-sm">
          <table className="w-full text-[12px] font-sans">
            <thead className="bg-hg-surface text-hg-text-dim text-[10px] uppercase tracking-wider">
              <tr>{['Facility', 'City', 'Tier', 'Owner', 'Status', 'Email', 'Phone', 'Last drip', 'Next send'].map((h) => (
                <th key={h} className="text-left px-2 py-2 font-normal whitespace-nowrap">{h}</th>))}</tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} onClick={() => setOpen(r.id)} data-testid={`partner-row-${r.id}`}
                  className={`border-t border-hg-border/60 cursor-pointer hover:bg-hg-surface ${open === r.id ? 'bg-hg-surface' : ''}`}>
                  <td className="px-2 py-1.5 text-hg-text inline-flex items-center gap-1.5"><Building2 size={11} className="text-hg-green" />{r.facility}</td>
                  <td className="px-2 py-1.5 text-hg-text-dim whitespace-nowrap">{[r.city, r.state].filter(Boolean).join(', ')}</td>
                  <td className="px-2 py-1.5 text-hg-text-dim whitespace-nowrap">{r.tier || '—'}</td>
                  <td className="px-2 py-1.5 text-hg-text-dim whitespace-nowrap">{r.owner}</td>
                  <td className="px-2 py-1.5 text-hg-text whitespace-nowrap">{r.status}</td>
                  <td className="px-2 py-1.5 text-hg-text-dim">{r.email || '—'}</td>
                  <td className="px-2 py-1.5 text-hg-text-dim whitespace-nowrap">{r.phone || '—'}</td>
                  <td className="px-2 py-1.5 text-hg-text-dim whitespace-nowrap">{r.drip?.last_step != null ? stepLabel(r.drip.last_step) : '—'}</td>
                  <td className="px-2 py-1.5 text-hg-text-dim whitespace-nowrap"><DripCell drip={r.drip} /></td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={9} className="px-2 py-6 text-center text-hg-text-dim italic">
                  {data.count === 0 ? 'No partner leads yet — run scripts/import_partner_leads.py --apply.' : 'No leads at that status.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <Pieces />
    </div>
  );
}
